import { Alert, Box } from '@customink/pigment-react';
import Link from '@customink/pigment-react/lib/components/Link';
import type { Product } from '@customink/project-service-react-components';
import { useConfigContext } from '@customink/project-service-react-components';
import type { ProjectItem } from '@customink/project-service-types';
import { useAtomValue } from 'jotai';

import { useOpenCatalogIframe } from '@/components/CatalogIframe';
import { WCIBMO_NAMES } from '@/constants';
import { useOrderQuantityType } from '@/hooks/useOrderQuantityType';
import { hasValidZipCodeAtom } from '@/hooks/useValidateZipCode';
import { pageViewTag } from '@/utils/metrics';

type AlertProps = {
  product?: Product;
  projectItem?: ProjectItem;
  onIncreaseQuantityClick?: () => void;
};

/**
 * The minimum order quantity is not met and there is no recommended alternative
 * style, so we offer a link to the no-minimum page in the catalog.
 */
export default function MinimumOrderQty({
  product,
  projectItem,
  onIncreaseQuantityClick,
}: AlertProps) {
  const catalogUrl = useConfigContext().catalogUrl;
  const url = `${catalogUrl}/no-minimum/96`;
  const quantityType = useOrderQuantityType();
  const hasValidZipCode = useAtomValue(hasValidZipCodeAtom);

  const openCatalogIframe = useOpenCatalogIframe();

  const onClick = () => {
    openCatalogIframe({ url, designCid: projectItem?.design?.cid });
  };

  if (!product) return null;

  if (hasValidZipCode) {
    pageViewTag(
      `checkout/summary/${quantityType}/wcibmo/${WCIBMO_NAMES.RAISE_ABOVE_MIN}`,
    );
  }

  return (
    <Alert data-testid="min-qty-alert" severity="error">
      <strong>
        The minimum order quantity for the {product.name} is{' '}
        {product.minimum_quantity}.
      </strong>{' '}
      Please either:
      <ul
        style={{
          fontSize: '14px',
          lineHeight: '1.5',
          marginTop: '4px',
          marginBottom: '0',
          paddingLeft: '16px',
        }}
      >
        <li style={{ marginBottom: '4px' }}>
          <Link onClick={onIncreaseQuantityClick}>
            Increase the order quantity
          </Link>{' '}
          to {product.minimum_quantity} or more, or
        </li>{' '}
        <li>
          choose a
          <Link onClick={onClick}> different product from the catalog</Link>{' '}
          that is available for any quantity.
        </li>
      </ul>
    </Alert>
  );
}
